import React, { useState } from "react";
import {
  Grid,
  Typography,
  Box,
  Button,
  FormGroup,
  FormLabel
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

import DocsIcon from "../../../../assets/main/docs-icon.svg";
import {
  DocsContainer,
  DateContainer,
  DateTextField
} from "./styles";

import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelIcon from "@mui/icons-material/Cancel";
import EditIcon from '@mui/icons-material/Edit';
import UpdateDocumentModal from "../UpdateDocumentModal";
import CustomTooltip from "../../../../components/common/CustomTooltip";

interface FacilityDocumentProps {
  document: any; // Replace 'any' with a more specific type if available
  onRefresh?: () => void;
  onSuccess?: () => void;
  onError?: (message: string) => void;
}

const FacilityDocument: React.FC<FacilityDocumentProps> = ({ document, onRefresh, onSuccess, onError }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    document_type,
    uploaded_document,
  } = document;

  const {
    document_url,
    // status,
    document_issued_date,
    document_expiring_date,
    is_accepted,
    is_rejected,
    remarks
  } = uploaded_document || {};

  const getDaysUntilExpiry = () => {
    if (!document_expiring_date) return null;
    const expiry = dayjs(document_expiring_date);
    const now = dayjs();
    return expiry.diff(now, 'day');
  };

  const daysUntilExpiry = getDaysUntilExpiry();
  const fileName = document_url ? document_url.split('/').pop() : "No file uploaded";

  const handleOpen = () => {
    setIsModalOpen(true);
  };

  const handleClose = () => {
    setIsModalOpen(false);
  };

  return (
    <DocsContainer>
      <Grid container>

        {/* LEFT SECTION — Document Name + File */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Grid container spacing={2} wrap="nowrap">
            <Grid sx={{ marginTop: 1 }}>
              <img src={DocsIcon} alt="Document Icon" />
            </Grid>

            <Grid>
              <Typography variant="subtitle1" fontWeight="bold">
                {document_type.document_type}
              </Typography>

              <Box display="flex" alignItems="center" gap={1}>
                <Typography
                  variant="subtitle2"
                  fontWeight="body2"
                  title={fileName}
                  sx={{ cursor: "pointer" }}
                  onClick={() => {
                    if (document_url) {
                      window.open(document_url, '_blank')
                    }
                  }}
                >
                  {fileName}
                </Typography>

                {/* Status Icon */}
                {is_accepted && (
                  <CustomTooltip title={remarks} arrow>
                    <CheckCircleRoundedIcon color="primary" />
                  </CustomTooltip>
                )}
                {is_rejected && (
                  <CustomTooltip title={remarks} arrow>
                    <CancelIcon color="error" />
                  </CustomTooltip>
                )}
                {/* No icon for 'pending' or other statuses */}
              </Box>
            </Grid>
          </Grid>
        </Grid>

        {/* RIGHT SECTION — Dates + Buttons */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Grid container>
            {/* DATES COLUMN */}
            <Grid size={{ xs: 12, md: 6 }}>
              <DateContainer>
                <FormGroup>
                  <FormLabel>Issued:</FormLabel>
                  <Box sx={{ position: 'relative' }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker format="DD/MM/YYYY"
                        value={document_issued_date ? dayjs(document_issued_date) : null}
                        disabled
                        enableAccessibleFieldDOMStructure={false}
                        slots={{ textField: DateTextField }}
                        slotProps={{
                          textField: { variant: "outlined", size: "small" },
                          popper: {
                            disablePortal: true,
                            sx: {
                              position: 'absolute',
                              top: '100%',
                              left: '0',
                              transform: 'none !important',
                              zIndex: 1300,
                            }
                          }
                        }}
                      />
                    </LocalizationProvider>
                  </Box>
                </FormGroup>

                <FormGroup>
                  <Typography variant="body2" sx={{ mx: 1, mt: 2 }}>
                    to
                  </Typography>
                </FormGroup>

                <FormGroup>
                  <FormLabel>Expires:</FormLabel>
                  <Box sx={{ position: 'relative' }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker format="DD/MM/YYYY"
                        value={document_expiring_date ? dayjs(document_expiring_date) : null}
                        disabled
                        enableAccessibleFieldDOMStructure={false}
                        slots={{ textField: DateTextField }}
                        slotProps={{
                          textField: { variant: "outlined", size: "small" },
                          popper: {
                            disablePortal: true,
                            sx: {
                              position: 'absolute',
                              top: '100%',
                              left: '0',
                              transform: 'none !important',
                              zIndex: 1300,
                            }
                          }
                        }}
                      />
                    </LocalizationProvider>
                  </Box>
                </FormGroup>
              </DateContainer>
            </Grid>

            {/* ACTION BUTTONS */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Box flexShrink={0} textAlign="right">
                {((uploaded_document && is_rejected) || (daysUntilExpiry !== null && daysUntilExpiry <= 30)) && (
                  <Button
                    variant="text"
                    color="primary"
                    startIcon={<EditIcon />}
                    sx={{ textTransform: "none", ml: 2 }}
                    onClick={handleOpen}
                  >
                    Edit
                  </Button>
                )}
                {!uploaded_document && (
                  <Button
                    variant="text"
                    color="primary"
                    startIcon={<EditIcon />}
                    sx={{ textTransform: "none", ml: 2 }}
                    onClick={handleOpen}
                  >
                    Add
                  </Button>
                )}
              </Box>
            </Grid>

          </Grid>
        </Grid>
      </Grid>

      {/* EXPIRY WARNING */}
      {uploaded_document && daysUntilExpiry !== null && daysUntilExpiry <= 30 && (
        <Grid container>
          <Box
            sx={{
              backgroundColor: "#fff3cd",
              padding: "8px 16px",
              marginTop: 2,
              borderRadius: "6px",
              width: "100%"
            }}
          >
            <Typography variant="body2">
              {daysUntilExpiry <= 0 ? 'Document has expired!' : `EXPIRES in ${daysUntilExpiry} days! Urgent action required.`}
            </Typography>
          </Box>
        </Grid>
      )}
      <UpdateDocumentModal
        open={isModalOpen}
        onClose={handleClose}
        document={document}
        onSuccess={onSuccess || onRefresh}
        onError={onError}
      />
    </DocsContainer>
  );
};

export default FacilityDocument;
